import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import '../../../styles/commerciale.css';

// ── Tipi ─────────────────────────────────────────────────────────────────────

type Canale = 'CONTANTI' | 'POS' | 'BONIFICO' | 'RIBA' | 'RID_SDD';

interface Rata {
  id?: number;
  ordine: number;
  dilazione: number;
  percentuale: number;
}

interface Condizione {
  id: number;
  societaId: number;
  nome: string;
  canale: Canale;
  fineMese: boolean;
  giorniMeseSuccessivo: number;
  flagAgoDic: boolean;
  giorniMeseSuccessivoAgoDic: number;
  ivaPrimaRata: boolean;
  attivo: boolean;
  rate: Rata[];
}

interface Scadenza {
  rata: number;
  percentuale: number;
  dataScadenza: string;
  descrizione: string;
  importo: number | null;
}

// ── Costanti ──────────────────────────────────────────────────────────────────

const CANALE_LABEL: Record<Canale, string> = {
  CONTANTI: 'Contanti', POS: 'POS', BONIFICO: 'Bonifico', RIBA: 'Ri.Ba.', RID_SDD: 'RID - SDD',
};
const CANALE_COLOR: Record<Canale, { bg: string; color: string }> = {
  CONTANTI: { bg: '#dcfce7', color: '#166534' },
  POS:      { bg: '#dbeafe', color: '#1e40af' },
  BONIFICO: { bg: '#ffedd5', color: '#9a3412' },
  RIBA:     { bg: '#f3e8ff', color: '#6b21a8' },
  RID_SDD:  { bg: '#cffafe', color: '#155e75' },
};
const DILAZIONI = [0, 30, 60, 90, 120, 150, 180];
const DIL_LABEL = (d: number) => d === 0 ? 'Immediato (0 gg)' : `${d} gg`;

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);

const fmtDate = (d: string | Date) =>
  new Date(d).toLocaleDateString('it-IT');

const today = () => new Date().toISOString().slice(0, 10);

// ── Form helpers ──────────────────────────────────────────────────────────────

function nuovaRata(ordine: number, dilazione = 60, percentuale = 0): Rata {
  return { ordine, dilazione, percentuale };
}

function distribuisciEquamente(nRate: number): number[] {
  const base = Math.floor(10000 / nRate) / 100;           // es. 3 rate → 33.33
  const resto = Math.round((100 - base * nRate) * 100) / 100; // es. 0.01
  const arr = Array(nRate).fill(base) as number[];
  arr[0] = Math.round((arr[0] + resto) * 100) / 100;      // prima rata prende il resto
  return arr;
}

// ── Componente principale ─────────────────────────────────────────────────────

export default function PagamentiPage() {
  const [condizioni, setCondizioni] = useState<Condizione[]>([]);
  const [loading,   setLoading]     = useState(true);
  const [error,     setError]       = useState('');

  // Filtri
  const [mostraDisattivi, setMostraDisattivi] = useState(false);

  const {
    societaId: fSocietaId,
    setSocietaId: setFSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
    loading: societaLoading,
  } = useFiltroSocieta();

  // Modal forma
  const [modalForm,  setModalForm]  = useState(false);
  const [editId,     setEditId]     = useState<number | null>(null);
  const [form,       setForm]       = useState(blankForm);
  const [nomeManuale, setNomeManuale] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState('');

  // Modal simulazione
  const [modalSim,    setModalSim]    = useState<Condizione | null>(null);
  const [simData,     setSimData]     = useState(today());
  const [simImporto,  setSimImporto]  = useState('1000');
  const [simImponibile, setSimImponibile] = useState('1000');
  const [simIva,      setSimIva]      = useState('220');
  const [simScadenze, setSimScadenze] = useState<Scadenza[]>([]);
  const [simLoading,  setSimLoading]  = useState(false);

  // Preview nel modal form (aggiornata solo su click "Simula")
  const [prevData,     setPrevData]     = useState(today());
  const [prevImporto,  setPrevImporto]  = useState('1000');
  const [prevImponibile, setPrevImponibile] = useState('1000');
  const [prevIva,      setPrevIva]      = useState('220');
  const [prevScadenze, setPrevScadenze] = useState<Scadenza[]>([]);
  const [prevLoading,  setPrevLoading]  = useState(false);
  const [prevSimError, setPrevSimError] = useState('');

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!pronta || societaLoading) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (fSocietaId) params.set('societaId', fSocietaId);
      if (!mostraDisattivi) params.set('attivo', 'true');

      const cRes = await apiFetch<{ condizioni: Condizione[] }>(`/api/pagamenti?${params}`);
      setCondizioni(cRes.condizioni ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [pronta, societaLoading, fSocietaId, mostraDisattivi]);

  useEffect(() => { load(); }, [load]);

  // ── Nome automatico ──────────────────────────────────────────────────────────

  const aggiornaNomeAuto = useCallback(async (f: typeof form) => {
    if (nomeManuale) return;
    try {
      const res = await apiFetch<{ nome: string }>('/api/pagamenti/anteprima-nome', {
        method: 'POST',
        body: JSON.stringify({
          canale: f.canale,
          dilazioni: f.rate.map(r => r.dilazione),
          fineMese: f.fineMese,
          giorniMeseSuccessivo: f.giorniMeseSuccessivo,
          flagAgoDic: f.flagAgoDic,
          giorniMeseSuccessivoAgoDic: f.giorniMeseSuccessivoAgoDic,
          ivaPrimaRata: f.ivaPrimaRata,
        }),
      });
      setForm(prev => ({ ...prev, nomeAuto: res.nome }));
    } catch {}
  }, [nomeManuale]);

  // Ricalcola nome automatico quando cambiano i parametri rilevanti (NON su nomeAuto/nomeCustom)
  const nomeKey = JSON.stringify({
    canale: form.canale,
    dilazioni: form.rate.map(r => r.dilazione),
    fineMese: form.fineMese,
    giorniMeseSuccessivo: form.giorniMeseSuccessivo,
    flagAgoDic: form.flagAgoDic,
    giorniMeseSuccessivoAgoDic: form.giorniMeseSuccessivoAgoDic,
    ivaPrimaRata: form.ivaPrimaRata,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (modalForm) aggiornaNomeAuto(form);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomeKey, modalForm, nomeManuale]);

  // ── Apertura modali ──────────────────────────────────────────────────────────

  const resetPreview = () => { setPrevScadenze([]); setPrevSimError(''); setPrevLoading(false); };

  const apriNuova = () => {
    const socId = fSocietaId ? Number(fSocietaId) : (societa[0]?.id ?? 0);
    setForm({ ...blankForm, societaId: socId });
    setEditId(null); setNomeManuale(false); setModalError(''); resetPreview(); setModalForm(true);
  };

  const apriModifica = (c: Condizione) => {
    setForm({
      societaId: c.societaId,
      canale: c.canale,
      fineMese: c.fineMese,
      giorniMeseSuccessivo: c.giorniMeseSuccessivo,
      flagAgoDic: c.flagAgoDic,
      giorniMeseSuccessivoAgoDic: c.giorniMeseSuccessivoAgoDic,
      ivaPrimaRata: c.ivaPrimaRata,
      rate: c.rate.map(r => ({ ordine: r.ordine, dilazione: r.dilazione, percentuale: Number(r.percentuale) })),
      nomeAuto: c.nome,
      nomeCustom: c.nome,
    });
    setEditId(c.id); setNomeManuale(true); setModalError(''); resetPreview(); setModalForm(true);
  };

  // ── Simula anteprima (solo su click, calcolo client-side) ─────────────────────

  const simulaPrev = () => {
    setPrevSimError('');
    if (!pctOk || form.rate.length === 0) {
      setPrevSimError('Completa correttamente il form prima di simulare');
      return;
    }
    setPrevLoading(true);
    setPrevScadenze([]);
    const importi = form.ivaPrimaRata
      ? { imponibile: Number(prevImponibile) || 0, iva: Number(prevIva) || 0 }
      : { importoTotale: Number(prevImporto) || 0 };
    const scadenze = calcolaScadenzeClient(prevData, form, importi);
    setPrevScadenze(scadenze);
    setPrevLoading(false);
  };

  // ── Salvataggio ──────────────────────────────────────────────────────────────

  const salva = async () => {
    const nome = nomeManuale && form.nomeCustom.trim() ? form.nomeCustom.trim() : form.nomeAuto;
    setSaving(true); setModalError('');
    try {
      const body = {
        societaId: form.societaId,
        nome,
        canale: form.canale,
        fineMese: form.fineMese,
        giorniMeseSuccessivo: form.giorniMeseSuccessivo,
        flagAgoDic: form.flagAgoDic,
        giorniMeseSuccessivoAgoDic: form.giorniMeseSuccessivoAgoDic,
        ivaPrimaRata: form.ivaPrimaRata,
        rate: form.rate.map((r, i) => ({ dilazione: r.dilazione, percentuale: r.percentuale, ordine: i + 1 })),
      };
      if (editId) {
        await apiFetch(`/api/pagamenti/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/pagamenti', { method: 'POST', body: JSON.stringify(body) });
      }
      setModalForm(false); load();
    } catch (e: any) { setModalError(e.message); }
    finally { setSaving(false); }
  };

  // ── Disattiva ────────────────────────────────────────────────────────────────

  const disattiva = async (c: Condizione) => {
    if (!window.confirm(`Disattivare "${c.nome}"?`)) return;
    try {
      await apiFetch(`/api/pagamenti/${c.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Simulazione ──────────────────────────────────────────────────────────────

  const apriSimula = (c: Condizione) => {
    setModalSim(c); setSimData(today());
    setSimImporto('1000'); setSimImponibile('1000'); setSimIva('220');
    setSimScadenze([]);
  };

  const calcola = async () => {
    if (!modalSim) return;
    setSimLoading(true);
    try {
      const importi = modalSim.ivaPrimaRata
        ? { imponibile: Number(simImponibile) || 0, iva: Number(simIva) || 0 }
        : { importoTotale: Number(simImporto) || 0 };
      const res = await apiFetch<{ scadenze: Scadenza[] }>('/api/pagamenti/calcola-scadenze-importo', {
        method: 'POST',
        body: JSON.stringify({ dataEmissione: simData, condizionePagamentoId: modalSim.id, ...importi }),
      });
      setSimScadenze(res.scadenze ?? []);
    } catch (e: any) { alert(e.message); }
    finally { setSimLoading(false); }
  };

  // ── Rate helpers ─────────────────────────────────────────────────────────────

  const updateRata = (i: number, field: keyof Rata, value: number) =>
    setForm(f => ({ ...f, rate: f.rate.map((r, j) => j === i ? { ...r, [field]: value } : r) }));

  const aggiungiRata = () =>
    setForm(f => ({ ...f, rate: [...f.rate, nuovaRata(f.rate.length + 1)] }));

  const rimuoviRata = (i: number) =>
    setForm(f => ({ ...f, rate: f.rate.filter((_, j) => j !== i).map((r, j) => ({ ...r, ordine: j + 1 })) }));

  const distribuisci = () => {
    const pct = distribuisciEquamente(form.rate.length);
    setForm(f => ({ ...f, rate: f.rate.map((r, i) => ({ ...r, percentuale: pct[i] })) }));
  };

  const canaleForza0 = form.canale === 'CONTANTI' || form.canale === 'POS';
  const totPct = form.rate.reduce((s, r) => s + r.percentuale, 0);
  const pctOk = Math.abs(totPct - 100) < 0.01;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Condizioni di Pagamento</h2>
          <p className="bop-page-subtitle">{condizioni.length} condizione{condizioni.length !== 1 ? 'i' : ''}</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={apriNuova}>+ Nuova Condizione</button>
      </div>

      {/* Filtri */}
      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={fSocietaId}
            onChange={e => setFSocietaId(e.target.value)}>
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <label className="commerciale-filtro-checkbox">
          <input type="checkbox" checked={mostraDisattivi}
            onChange={e => setMostraDisattivi(e.target.checked)} />
          Mostra disattivati
        </label>
      </div>

      {error && <div className="inline-error">{error}</div>}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? (
            <div className="inline-loading">Caricamento…</div>
          ) : condizioni.length === 0 ? (
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">Nessuna condizione di pagamento configurata.</p>
            </div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Canale</th>
                  <th>Rate / Dilazioni</th>
                  <th className="commerciale-table-center">Fine Mese</th>
                  <th className="commerciale-table-center">FM / Ago-Dic</th>
                  <th className="commerciale-table-center">IVA 1ª rata</th>
                  <th>Percentuali</th>
                  <th>Stato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {condizioni.map(c => {
                  const rateLabel = c.rate.length === 0 ? '—'
                    : c.rate.map(r => r.dilazione === 0 ? 'Immediato' : `${r.dilazione} gg`).join(' — ');
                  const pctLabel = c.rate.map(r => `${Number(r.percentuale)}%`).join(' + ');
                  const badgeClass = `commerciale-badge-canale commerciale-badge-${c.canale.toLowerCase()}`;
                  return (
                    <tr key={c.id} className={!c.attivo ? 'commerciale-tr-inactive' : ''}>
                      <td className="commerciale-td-bold">{c.nome}</td>
                      <td><span className={badgeClass}>{CANALE_LABEL[c.canale]}</span></td>
                      <td className="commerciale-table-text-sm">{rateLabel}</td>
                      <td className="commerciale-table-center commerciale-table-text-base">
                        {c.fineMese
                          ? <span className="commerciale-check-icon">✓</span>
                          : <span className="commerciale-dash-icon">—</span>}
                      </td>
                      <td className="commerciale-table-center commerciale-table-text-sm">
                        {(() => {
                          const parts: string[] = [];
                          if (c.giorniMeseSuccessivo > 0) parts.push(`+${c.giorniMeseSuccessivo}`);
                          if (c.flagAgoDic && c.giorniMeseSuccessivoAgoDic > 0)
                            parts.push(`ago/dic+${c.giorniMeseSuccessivoAgoDic}`);
                          return parts.length > 0 ? parts.join(' / ') : '—';
                        })()}
                      </td>
                      <td className="commerciale-table-center commerciale-table-text-base">
                        {c.ivaPrimaRata
                          ? <span className="commerciale-check-icon">✓</span>
                          : <span className="commerciale-dash-icon">—</span>}
                      </td>
                      <td className="commerciale-table-text-sm">{pctLabel}</td>
                      <td>
                        <span className={c.attivo ? 'bop-badge bop-badge--success' : 'bop-badge bop-badge--neutral'}>
                          {c.attivo ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                      <td>
                        <div className="commerciale-td-actions">
                          <button className="bop-btn bop-btn-ghost" onClick={() => apriModifica(c)}>Modifica</button>
                          <button className="bop-btn bop-btn-ghost" onClick={() => apriSimula(c)}>Simula</button>
                          {c.attivo && (
                            <button className="bop-btn bop-btn-ghost commerciale-btn-danger-text" onClick={() => disattiva(c)}>
                              Disattiva
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal Form ── */}
      {modalForm && (
        <div className="commerciale-modal-overlay">
          <div className="commerciale-modal-container">
            <div className="commerciale-modal-header">
              <h3 className="commerciale-modal-title">{editId ? 'Modifica Condizione' : 'Nuova Condizione'}</h3>
              <button className="commerciale-modal-close" onClick={() => setModalForm(false)}>×</button>
            </div>

            <div className="commerciale-modal-grid">
              {/* Colonna sinistra: Form */}
              <div className="commerciale-modal-body">
                {modalError && <div className="inline-error">{modalError}</div>}

                {!isUnica && !editId && (
                  <div className="bop-form-group">
                    <label className="bop-label">Società <span className="bop-text-danger">*</span></label>
                    <select className="bop-select" value={form.societaId}
                      onChange={e => setForm(f => ({ ...f, societaId: Number(e.target.value) }))}>
                      {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                )}

                <div className="bop-form-group">
                  <label className="bop-label">Canale pagamento <span className="bop-text-danger">*</span></label>
                  <select className="bop-select" value={form.canale}
                    onChange={e => {
                      const c = e.target.value as Canale;
                      const forza0 = c === 'CONTANTI' || c === 'POS';
                      setForm(f => ({
                        ...f, canale: c,
                        rate: forza0 ? [{ ordine: 1, dilazione: 0, percentuale: 100 }] : f.rate,
                        fineMese: forza0 ? false : f.fineMese,
                        giorniMeseSuccessivo: forza0 ? 0 : f.giorniMeseSuccessivo,
                        flagAgoDic: forza0 ? false : f.flagAgoDic,
                        giorniMeseSuccessivoAgoDic: forza0 ? 0 : f.giorniMeseSuccessivoAgoDic,
                        ivaPrimaRata: forza0 ? false : f.ivaPrimaRata,
                      }));
                    }}>
                    {(Object.keys(CANALE_LABEL) as Canale[]).map(c => (
                      <option key={c} value={c}>{CANALE_LABEL[c]}</option>
                    ))}
                  </select>
                </div>

                {/* Rate */}
                <div className="bop-form-group">
                  <label className="bop-label">Rate di pagamento <span className="bop-text-danger">*</span></label>
                  <table className="commerciale-rate-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Dilazione</th>
                        <th>%</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.rate.map((r, i) => (
                        <tr key={i}>
                          <td className="commerciale-rate-row-num">{i + 1}</td>
                          <td>
                            {canaleForza0 ? (
                              <span className="bop-text-muted">Immediato</span>
                            ) : (
                              <select className="bop-select commerciale-rate-select"
                                value={r.dilazione} onChange={e => updateRata(i, 'dilazione', Number(e.target.value))}>
                                {DILAZIONI.map(d => <option key={d} value={d}>{DIL_LABEL(d)}</option>)}
                              </select>
                            )}
                          </td>
                          <td>
                            <div className="commerciale-rate-input-container">
                              <input type="number" min={0} max={100} step={0.01}
                                className="commerciale-rate-input"
                                value={r.percentuale}
                                onChange={e => updateRata(i, 'percentuale', Number(e.target.value))} />
                              <span className="commerciale-rate-percent">%</span>
                            </div>
                          </td>
                          <td>
                            {!canaleForza0 && form.rate.length > 1 && (
                              <button className="commerciale-rate-remove" onClick={() => rimuoviRata(i)}>×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totale percentuali */}
                  <div className="commerciale-rate-totals">
                    <span className={`commerciale-rate-total ${pctOk ? 'valid' : 'invalid'}`}>
                      Totale: {totPct.toFixed(2)}% {pctOk ? '✓' : '✗ (deve essere 100%)'}
                    </span>
                    <button className="bop-btn bop-btn-ghost commerciale-btn-distribute" onClick={distribuisci}>
                      Distribuisci equamente
                    </button>
                  </div>

                  {!canaleForza0 && (
                    <button className="bop-btn bop-btn-secondary commerciale-btn-add-rate"
                      onClick={aggiungiRata} disabled={form.rate.some(r => r.dilazione === 0)}>
                      + Aggiungi Rata
                    </button>
                  )}
                </div>

                {/* Fine mese */}
                {!canaleForza0 && (
                  <>
                    <div className="bop-form-group">
                      <label className="commerciale-checkbox-label">
                        <input type="checkbox" checked={form.fineMese}
                          onChange={e => setForm(f => ({ ...f, fineMese: e.target.checked, giorniMeseSuccessivo: e.target.checked ? f.giorniMeseSuccessivo : 0 }))} />
                        <span>Fine mese</span>
                      </label>
                      <div className="commerciale-help-text">
                        La scadenza viene spostata all'ultimo giorno del mese
                      </div>
                    </div>

                    {form.fineMese && (
                      <div className="bop-form-group commerciale-finemese-nested">
                        <div className="commerciale-finemese-row">
                          <span className="commerciale-finemese-label">Giorni mese successivo</span>
                          <input
                            type="number"
                            className="bop-input commerciale-number-input-sm"
                            min={0} max={31}
                            value={form.giorniMeseSuccessivo}
                            onChange={e => setForm(f => ({ ...f, giorniMeseSuccessivo: Number(e.target.value) }))}
                          />
                          <span className="commerciale-help-inline">
                            {form.flagAgoDic ? 'gg (tutti i mesi esclusi ago/dic)' : 'gg per tutte le scadenze'}
                          </span>
                        </div>
                        <div className="commerciale-help-text-simple">
                          Giorni da aggiungere dopo la fine del mese
                        </div>

                        <div className="commerciale-agodic-box">
                          <label className="commerciale-checkbox-label">
                            <input
                              type="checkbox"
                              checked={form.flagAgoDic}
                              onChange={e => setForm(f => ({
                                ...f,
                                flagAgoDic: e.target.checked,
                                giorniMeseSuccessivoAgoDic: e.target.checked ? f.giorniMeseSuccessivoAgoDic : 0,
                              }))}
                            />
                            <span>Solo agosto/dicembre</span>
                          </label>
                          <div className="commerciale-help-text">
                            Scadenze di fine agosto e fine dicembre: usa giorni aggiuntivi dedicati
                            (al posto di quelli sopra)
                          </div>

                          {form.flagAgoDic && (
                            <div className="commerciale-finemese-row commerciale-finemese-row-indent">
                              <span className="commerciale-finemese-label">Giorni aggiuntivi</span>
                              <input
                                type="number"
                                className="bop-input commerciale-number-input-sm"
                                min={1} max={31}
                                value={form.giorniMeseSuccessivoAgoDic}
                                onChange={e => setForm(f => ({ ...f, giorniMeseSuccessivoAgoDic: Number(e.target.value) }))}
                              />
                              <span className="commerciale-help-inline">gg (31/08 e 31/12)</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* IVA sulla prima rata */}
                {!canaleForza0 && (
                  <div className="bop-form-group">
                    <label className="commerciale-checkbox-label">
                      <input type="checkbox" checked={form.ivaPrimaRata}
                        onChange={e => setForm(f => ({ ...f, ivaPrimaRata: e.target.checked }))} />
                      <span>Tutta l'IVA sulla prima rata</span>
                    </label>
                    <div className="commerciale-help-text">
                      L'imponibile viene ripartito tra le rate secondo le percentuali;
                      l'intera IVA viene caricata sulla prima rata. La simulazione richiede
                      imponibile e IVA separati.
                    </div>
                  </div>
                )}

                {/* Nome */}
                <div className="bop-form-group">
                  <label className="bop-label">Nome condizione</label>
                  <div className="commerciale-input-with-btn">
                    <input className="bop-input commerciale-input-flex"
                      value={nomeManuale ? form.nomeCustom : form.nomeAuto}
                      onChange={e => { setNomeManuale(true); setForm(f => ({ ...f, nomeCustom: e.target.value })); }}
                      placeholder="Generato automaticamente" />
                    {nomeManuale && (
                      <button className="bop-btn bop-btn-ghost commerciale-btn-regen"
                        onClick={() => setNomeManuale(false)} title="Rigenera automaticamente">
                        ↺ Rigenera
                      </button>
                    )}
                  </div>
                  <div className="commerciale-help-text-simple">
                    Il nome viene generato automaticamente dai parametri
                  </div>
                </div>
              </div>

              {/* Colonna destra: Preview */}
              <div className="commerciale-modal-sidebar">
                <div className="commerciale-modal-sidebar-title">Anteprima scadenze</div>

                <div className="bop-form-group">
                  <label className="commerciale-modal-sidebar-label">Data emissione esempio</label>
                  <input type="date" className="bop-input"
                    value={prevData} onChange={e => setPrevData(e.target.value)} />
                </div>

                {form.ivaPrimaRata ? (
                  <>
                    <div className="bop-form-group">
                      <label className="commerciale-modal-sidebar-label">Imponibile esempio (€)</label>
                      <input type="number" className="bop-input"
                        value={prevImponibile} onChange={e => setPrevImponibile(e.target.value)} />
                    </div>
                    <div className="bop-form-group">
                      <label className="commerciale-modal-sidebar-label">IVA esempio (€)</label>
                      <input type="number" className="bop-input"
                        value={prevIva} onChange={e => setPrevIva(e.target.value)} />
                    </div>
                    <div className="commerciale-sim-totale-row">
                      <span className="bop-text-muted">Totale</span>
                      <span className="commerciale-mono">{fmt((Number(prevImponibile) || 0) + (Number(prevIva) || 0))}</span>
                    </div>
                  </>
                ) : (
                  <div className="bop-form-group">
                    <label className="commerciale-modal-sidebar-label">Importo esempio (€)</label>
                    <input type="number" className="bop-input"
                      value={prevImporto} onChange={e => setPrevImporto(e.target.value)} />
                  </div>
                )}

                <button className="bop-btn bop-btn-secondary" onClick={simulaPrev} disabled={prevLoading}>
                  {prevLoading ? 'Calcolo…' : '▶ Simula'}
                </button>

                {prevSimError && (
                  <div className="commerciale-modal-error">{prevSimError}</div>
                )}

                {prevScadenze.length === 0 && !prevSimError ? (
                  <div className="commerciale-modal-sidebar-help">
                    Clicca "Simula" per calcolare le scadenze
                  </div>
                ) : prevScadenze.length > 0 ? (
                  <>
                    <table className="commerciale-sim-table commerciale-sim-table-sm">
                      <thead>
                        <tr>
                          <th>Rata</th>
                          <th>Scadenza</th>
                          <th className="bop-text-right">Importo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prevScadenze.map(s => (
                          <tr key={s.rata}>
                            <td className="bop-text-muted">{s.rata}</td>
                            <td>{fmtDate(s.dataScadenza)}</td>
                            <td className="commerciale-mono bop-text-right">
                              {s.importo != null ? fmt(s.importo) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={2} className="bop-text-muted">Totale</td>
                          <td className="commerciale-mono bop-text-right">
                            {fmt(prevScadenze.reduce((s, x) => s + (x.importo ?? 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div className="commerciale-sim-description">
                      {prevScadenze[0]?.descrizione}
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div className="commerciale-modal-footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalForm(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving || !pctOk}>
                {saving ? 'Salvataggio…' : (editId ? 'Salva Modifiche' : 'Crea Condizione')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Simula ── */}
      {modalSim && (
        <div className="commerciale-modal-overlay">
          <div className="commerciale-modal-container commerciale-modal-container-sm">
            <div className="commerciale-modal-header">
              <h3 className="commerciale-modal-title">Simula Scadenze</h3>
              <button className="commerciale-modal-close" onClick={() => setModalSim(null)}>×</button>
            </div>
            <div className="commerciale-modal-body-simple">
              <div className="commerciale-sim-title">{modalSim.nome}</div>

              <div className="commerciale-sim-fields">
                <div className="bop-form-group commerciale-sim-field">
                  <label className="bop-label">Data emissione</label>
                  <input type="date" className="bop-input" value={simData}
                    onChange={e => setSimData(e.target.value)} />
                </div>
                {modalSim.ivaPrimaRata ? (
                  <>
                    <div className="bop-form-group commerciale-sim-field-sm">
                      <label className="bop-label">Imponibile (€)</label>
                      <input type="number" className="bop-input" value={simImponibile}
                        onChange={e => setSimImponibile(e.target.value)} />
                    </div>
                    <div className="bop-form-group commerciale-sim-field-sm">
                      <label className="bop-label">IVA (€)</label>
                      <input type="number" className="bop-input" value={simIva}
                        onChange={e => setSimIva(e.target.value)} />
                    </div>
                    <div className="bop-form-group commerciale-sim-field-sm">
                      <label className="bop-label">Totale (€)</label>
                      <input type="text" className="bop-input" readOnly
                        value={fmt((Number(simImponibile) || 0) + (Number(simIva) || 0))} />
                    </div>
                  </>
                ) : (
                  <div className="bop-form-group commerciale-sim-field-sm">
                    <label className="bop-label">Importo totale (€)</label>
                    <input type="number" className="bop-input" value={simImporto}
                      onChange={e => setSimImporto(e.target.value)} />
                  </div>
                )}
              </div>

              <button className="bop-btn bop-btn-primary commerciale-btn-calc" onClick={calcola} disabled={simLoading}>
                {simLoading ? 'Calcolo…' : 'Calcola'}
              </button>

              {simScadenze.length > 0 && (
                <table className="commerciale-sim-table">
                  <thead>
                    <tr>
                      <th>Rata</th>
                      <th>Scadenza</th>
                      <th className="bop-text-right">Importo</th>
                      <th className="bop-text-right">%</th>
                      <th>Descrizione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simScadenze.map(s => {
                      const scaduta = new Date(s.dataScadenza) < new Date();
                      return (
                        <tr key={s.rata} className={scaduta ? 'commerciale-sim-row-scaduta' : ''}>
                          <td className="commerciale-sim-td-bold">{s.rata}</td>
                          <td className={scaduta ? 'bop-text-danger' : ''}>
                            {fmtDate(s.dataScadenza)}
                            {scaduta && <span className="commerciale-sim-label-scaduta">scaduta</span>}
                          </td>
                          <td className="commerciale-mono bop-text-right">
                            {s.importo != null ? fmt(s.importo) : '—'}
                          </td>
                          <td className="bop-text-muted bop-text-right">
                            {s.percentuale}%
                          </td>
                          <td className="bop-text-muted">{s.descrizione}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="bop-text-muted">Totale</td>
                      <td className="commerciale-mono bop-text-right">
                        {fmt(simScadenze.reduce((s, x) => s + (x.importo ?? 0), 0))}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
            <div className="commerciale-modal-footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalSim(null)}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ── Stato form blank ──────────────────────────────────────────────────────────

const blankForm = {
  societaId: 0,
  canale: 'BONIFICO' as Canale,
  fineMese: false,
  giorniMeseSuccessivo: 0,
  flagAgoDic: false,
  giorniMeseSuccessivoAgoDic: 0,
  ivaPrimaRata: false,
  rate: [{ ordine: 1, dilazione: 60, percentuale: 100 }] as Rata[],
  nomeAuto: '',
  nomeCustom: '',
};

// ── Calcolo scadenze client-side (fallback preview) ───────────────────────────

function calcolaScadenzeClient(
  dataEmissione: string,
  form: typeof blankForm,
  importi: { importoTotale?: number; imponibile?: number; iva?: number },
): Scadenza[] {
  const ivaPrimaRata = form.ivaPrimaRata && importi.imponibile != null;
  const totaleClassico = importi.importoTotale != null
    ? importi.importoTotale
    : (importi.imponibile != null ? importi.imponibile + (importi.iva ?? 0) : 0);
  const base = new Date(dataEmissione);
  return form.rate.map((r, i) => {
    let d = new Date(base);
    d.setDate(d.getDate() + r.dilazione);
    if (form.fineMese) {
      d = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    // Step 3: logica ago/dic (specchiare backend)
    const meseFineMese = d.getMonth();
    const isAgoDic = form.flagAgoDic &&
                     form.giorniMeseSuccessivoAgoDic > 0 &&
                     (meseFineMese === 7 || meseFineMese === 11);

    if (isAgoDic) {
      d = new Date(d);
      d.setDate(d.getDate() + form.giorniMeseSuccessivoAgoDic);
    } else if (form.giorniMeseSuccessivo > 0) {
      d = new Date(d);
      d.setDate(d.getDate() + form.giorniMeseSuccessivo);
    }
    let importo: number;
    if (ivaPrimaRata) {
      importo = Math.round((importi.imponibile ?? 0) * r.percentuale) / 100;
      if (i === 0) importo = Math.round((importo + (importi.iva ?? 0)) * 100) / 100;
    } else {
      importo = Math.round(totaleClassico * r.percentuale) / 100;
    }
    const parts = [`${r.percentuale}%`, r.dilazione === 0 ? 'immediato' : `${r.dilazione} gg`];
    if (form.fineMese) {
      if (isAgoDic) {
        parts.push(`FM ago/dic+${form.giorniMeseSuccessivoAgoDic} ms`);
      } else if (form.giorniMeseSuccessivo > 0) {
        parts.push(`FM+${form.giorniMeseSuccessivo}`);
      } else {
        parts.push('FM');
      }
    }
    return { rata: i + 1, percentuale: r.percentuale, dataScadenza: d.toISOString(), descrizione: parts.join(' — '), importo };
  });
}
